import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Button, Input, Space, Tooltip, Alert, Typography, message } from 'antd';
import {
  PlusOutlined, DeleteOutlined, CloseCircleFilled,
  ClearOutlined, CheckCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * Planilla editable de carga masiva. Pensada como abstraccion reusable
 * para "cargar muchas filas de pocas columnas" con UX tipo Excel ligero:
 * editar inline, pegar desde el portapapeles (tab + newline), validar
 * por celda, eliminar filas.
 *
 * La usan Familiares (alta del catalogo, 3 columnas) y las Crias de la
 * ficha del animal (7 columnas, EDITA el set ya cargado: por eso existen
 * `initialRows` y `permitirVacio`).
 *
 * Props:
 *   columns: [{
 *     key:       string  // identificador interno (matchea el body)
 *     label:     string  // header de la columna
 *     required?: boolean
 *     width?:    number
 *     placeholder?: string
 *     // validador opcional. Retorna string con el error o null si OK.
 *     // Recibe el valor, todas las filas y el id de la fila actual para
 *     // poder detectar duplicados intra-planilla.
 *     validate?: (value, allRows, currentRowId) => string | null
 *   }]
 *   onSubmit:        async (rows: Array<{[key]: string}>) => void
 *                    // recibe SOLO filas no vacias y ya validadas
 *   submitLabel?:    string   // texto del boton final
 *   initialRowCount?: number  // filas vacias iniciales (default 5)
 *   maxRows?:        number   // tope de seguridad (default 500)
 *   // Errores devueltos por el servidor mapeados a filas (1-based fila).
 *   // Forma: [{ fila, codigo, error, sugerencia?, ...resto }]
 *   serverErrors?:   Array<{ fila: number, error: string, sugerencia?: string }>
 *   // Filas ya cargadas, para EDITAR un set en vez de dar de alta uno nuevo:
 *   // [{ [key]: string }]. Se leen al montar; para recargarlas, cambiar el
 *   // `key` del componente.
 *   initialRows?:    Array<{[key]: string}>
 *   // Deja guardar la planilla sin filas (= borrar todo lo cargado). En un
 *   // alta no tiene sentido; en un reemplazo del set completo, si.
 *   permitirVacio?:  boolean
 */
const PlanillaCarga = ({
  columns,
  onSubmit,
  submitLabel = 'Cargar',
  initialRowCount = 5,
  maxRows = 500,
  serverErrors = [],
  initialRows = null,
  permitirVacio = false,
}) => {
  // Contador interno para ids unicos de fila (sin librerias).
  const nextIdRef = useRef(0);

  const makeEmptyRow = useCallback(() => {
    const id = `row-${nextIdRef.current++}`;
    const cells = {};
    columns.forEach(c => { cells[c.key] = ''; });
    return { id, cells };
  }, [columns]);

  const [rows, setRows] = useState(() => {
    if (!initialRows?.length) {
      return Array.from({ length: initialRowCount }, () => makeEmptyRow());
    }
    const cargadas = initialRows.map(fila => {
      const row = makeEmptyRow();
      columns.forEach(c => { row.cells[c.key] = fila[c.key] == null ? '' : String(fila[c.key]); });
      return row;
    });
    // Una vacía al final, para seguir cargando sin apretar «Agregar fila».
    return [...cargadas, makeEmptyRow()];
  });
  const [submitting, setSubmitting] = useState(false);

  // Mapea fila 1-based del backend al row.id actual.
  // OJO: el orden de las filas en submit puede no coincidir con el orden
  // actual si el usuario las reordeno. Asumimos que NO se reordenan
  // entre submit y respuesta (es asincrono pero la planilla queda
  // bloqueada por submitting).
  const lastSubmittedRowIds = useRef([]);
  const serverErrorsByRowId = useMemo(() => {
    const map = {};
    serverErrors.forEach(e => {
      const idx = e.fila - 1;
      const rowId = lastSubmittedRowIds.current[idx];
      if (rowId) map[rowId] = e;
    });
    return map;
  }, [serverErrors]);

  // Es una fila vacia (todas las celdas en blanco)?
  const isRowEmpty = useCallback(
    (row) => columns.every(c => !(row.cells[c.key] || '').trim()),
    [columns],
  );

  // Calcula errores cliente-side de una fila, indexados por column key.
  const validateRow = useCallback((row, allRows) => {
    if (isRowEmpty(row)) return {};   // filas vacias no se validan ni se mandan
    const errors = {};
    columns.forEach(c => {
      const value = (row.cells[c.key] || '').trim();
      if (c.required && !value) {
        errors[c.key] = `${c.label} es requerido`;
        return;
      }
      if (value && c.validate) {
        const err = c.validate(value, allRows, row.id);
        if (err) errors[c.key] = err;
      }
    });
    return errors;
  }, [columns, isRowEmpty]);

  // Errores cliente-side recalculados en cada render.
  const errorsByRowId = useMemo(() => {
    const map = {};
    rows.forEach(r => { map[r.id] = validateRow(r, rows); });
    return map;
  }, [rows, validateRow]);

  const handleCellChange = (rowId, colKey, value) => {
    setRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, cells: { ...r.cells, [colKey]: value } } : r
    ));
  };

  // Pega desde el portapapeles. Si lo pegado tiene tabs o newlines, se
  // distribuye en N celdas a la derecha y/o N filas abajo a partir de
  // la celda anclada. Si es un solo valor, deja que el Input lo maneje
  // (no hacemos preventDefault).
  const handlePaste = (e, rowId, colKey) => {
    const text = e.clipboardData.getData('text');
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;
    e.preventDefault();

    const matrix = text
      .replace(/\r/g, '')
      .split('\n')
      .filter((line, idx, arr) => !(idx === arr.length - 1 && line === ''))  // tail newline
      .map(line => line.split('\t'));

    const startRowIdx = rows.findIndex(r => r.id === rowId);
    const startColIdx = columns.findIndex(c => c.key === colKey);
    if (startRowIdx < 0 || startColIdx < 0) return;

    setRows(prev => {
      const next = [...prev];
      matrix.forEach((line, dr) => {
        const targetIdx = startRowIdx + dr;
        // Auto-crear filas si nos pasamos del final, respetando maxRows.
        while (next.length <= targetIdx && next.length < maxRows) {
          next.push(makeEmptyRow());
        }
        if (targetIdx >= next.length) return;  // alcanzamos el cap
        const cells = { ...next[targetIdx].cells };
        line.forEach((cellValue, dc) => {
          const targetCol = columns[startColIdx + dc];
          if (targetCol) cells[targetCol.key] = cellValue;
        });
        next[targetIdx] = { ...next[targetIdx], cells };
      });
      return next;
    });

    if (rows.length + matrix.length > maxRows) {
      message.warning(`Se alcanzo el limite de ${maxRows} filas. Las filas extra se descartaron.`);
    }
  };

  const handleAddRow = () => {
    if (rows.length >= maxRows) {
      message.warning(`Limite de ${maxRows} filas alcanzado`);
      return;
    }
    setRows(prev => [...prev, makeEmptyRow()]);
  };

  const handleDeleteRow = (rowId) => {
    setRows(prev => prev.length > 1
      ? prev.filter(r => r.id !== rowId)
      : [makeEmptyRow()]   // siempre al menos 1 fila visible
    );
  };

  const handleClearAll = () => {
    setRows(Array.from({ length: initialRowCount }, () => makeEmptyRow()));
  };

  // Filas no vacias que serian enviadas.
  const filasUtiles = useMemo(
    () => rows.filter(r => !isRowEmpty(r)),
    [rows, isRowEmpty],
  );

  // Cuenta de errores cliente-side en filas no vacias.
  const totalErroresCliente = useMemo(() =>
    filasUtiles.reduce((sum, r) => sum + Object.keys(errorsByRowId[r.id] || {}).length, 0),
    [filasUtiles, errorsByRowId]
  );

  const handleSubmit = async () => {
    if (filasUtiles.length === 0 && !permitirVacio) {
      message.warning('No hay filas para cargar');
      return;
    }
    if (totalErroresCliente > 0) {
      message.error('Hay errores en la planilla. Corregilos antes de cargar.');
      return;
    }
    setSubmitting(true);
    lastSubmittedRowIds.current = filasUtiles.map(r => r.id);
    try {
      // Limpiar trims en el payload final
      const payload = filasUtiles.map(r => {
        const out = {};
        columns.forEach(c => {
          out[c.key] = (r.cells[c.key] || '').trim();
        });
        return out;
      });
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Toolbar */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<PlusOutlined />} onClick={handleAddRow}>
          Agregar fila
        </Button>
        <Button icon={<ClearOutlined />} onClick={handleClearAll}>
          Limpiar
        </Button>
        <Text type="secondary">
          Tip: copia desde Excel/Sheets y pega en cualquier celda. La planilla
          se expande sola.
        </Text>
      </Space>

      {/* Resumen */}
      <Space style={{ marginBottom: 8 }}>
        <Text>
          <strong>{filasUtiles.length}</strong> filas con datos
        </Text>
        {totalErroresCliente > 0 && (
          <Text type="danger">
            <CloseCircleFilled /> {totalErroresCliente} errores
          </Text>
        )}
        {totalErroresCliente === 0 && filasUtiles.length > 0 && (
          <Text type="success">
            <CheckCircleOutlined /> Sin errores
          </Text>
        )}
      </Space>

      {/* Errores del servidor */}
      {serverErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="El servidor rechazo la carga"
          description={
            <span>
              Hay <strong>{serverErrors.length}</strong> filas con errores.
              Se marcan abajo con borde rojo y mensaje al pasar el cursor.
              No se inserto nada (carga all-or-nothing).
            </span>
          }
        />
      )}

      {/* Planilla */}
      <div style={{ overflowX: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 600 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={th(50)}>#</th>
              {columns.map(c => (
                <th key={c.key} style={th(c.width || 200)}>
                  {c.label}{c.required && <span style={{ color: '#ff4d4f' }}> *</span>}
                </th>
              ))}
              <th style={th(50)}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const clientErrs = errorsByRowId[row.id] || {};
              const serverErr = serverErrorsByRowId[row.id];
              const hasClientErr = Object.keys(clientErrs).length > 0;
              // Para el iconito + numero rojo en la columna #: cualquier
              // error (cliente o servidor) tira la misma seña visual.
              const hasAnyError = hasClientErr || !!serverErr;
              const rowErrorTooltip = serverErr
                ? serverErr.error + (serverErr.sugerencia ? ` (sugerencia: ${serverErr.sugerencia})` : '')
                : (hasClientErr ? 'Hay errores en esta fila — pasa el cursor por las celdas' : null);
              return (
                <tr key={row.id}>
                  <td style={tdIdx(hasAnyError)}>
                    {idx + 1}
                    {hasAnyError && (
                      <Tooltip title={rowErrorTooltip}>
                        <CloseCircleFilled style={{ color: '#ff4d4f', marginLeft: 4 }} />
                      </Tooltip>
                    )}
                  </td>
                  {columns.map(c => {
                    const err = clientErrs[c.key];
                    return (
                      <td key={c.key} style={td(err)}>
                        <Tooltip title={err} open={err ? undefined : false}>
                          <Input
                            size="small"
                            placeholder={c.placeholder}
                            value={row.cells[c.key] || ''}
                            onChange={e => handleCellChange(row.id, c.key, e.target.value)}
                            onPaste={e => handlePaste(e, row.id, c.key)}
                            status={err ? 'error' : ''}
                            disabled={submitting}
                            // Cuando hay error dejamos que antd pinte el borde rojo de
                            // status='error'. Sin error, sacamos el borde para look
                            // 'spreadsheet' (las celdas estan delimitadas por la table).
                            style={err ? undefined : { border: 'none', boxShadow: 'none' }}
                          />
                        </Tooltip>
                      </td>
                    );
                  })}
                  <td style={td()}>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteRow(row.id)}
                      disabled={submitting}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Submit */}
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button
          type="primary"
          size="large"
          loading={submitting}
          onClick={handleSubmit}
          disabled={(filasUtiles.length === 0 && !permitirVacio) || totalErroresCliente > 0}
        >
          {submitLabel} ({filasUtiles.length})
        </Button>
      </div>
    </div>
  );
};

// Helpers de estilos inline (chicos, no merecen StyleSheet)
const th = (w) => ({
  padding: '8px 10px', textAlign: 'left', fontWeight: 600,
  borderBottom: '1px solid #f0f0f0', width: w, minWidth: w,
});
const td = (hasError) => ({
  padding: 2,
  borderBottom: '1px solid #fafafa',
  verticalAlign: 'middle',
  background: hasError ? '#fff2f0' : 'transparent',
});
const tdIdx = (hasError) => ({
  padding: '4px 8px',
  borderBottom: '1px solid #fafafa',
  color: hasError ? '#ff4d4f' : '#999',
  fontSize: 12,
  textAlign: 'center',
  fontWeight: hasError ? 600 : 400,
  background: hasError ? '#fff2f0' : 'transparent',
});

export default PlanillaCarga;
